import React, { useState, useEffect } from 'react';
import { safeJson, networkMessage } from '../apiHelpers';
import { t, getLang, LANGUAGES } from '../i18n';

function Navbar({ user, onLogin, onLogout }) {
  const [lang, setLangState] = React.useState(getLang());
  React.useEffect(() => {
    const interval = setInterval(() => setLangState(getLang()), 300);
    return () => clearInterval(interval);
  }, []);
  const tr = (key) => {
    const { T } = require('../i18n');
    return T[lang]?.[key] || T['en'][key] || key;
  };
  const currentLang = getLang();
  const isRTL = LANGUAGES[currentLang]?.dir === 'rtl';
  const [menuOpen, setMenuOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [notifs, setNotifs] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);

  const fetchNotifications = async () => {
    try {
      const res = await fetch('/api/notifications/', {
        headers: { 'Authorization': `Token ${user.token}` }
      });
      const data = await safeJson(res);
      setUnread(data.unread || 0);
      setNotifs(data.notifications || []);
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    if (user) fetchNotifications();
    const interval = setInterval(() => { if (user) fetchNotifications(); }, 10000);
    return () => clearInterval(interval);
  }, [user]);

  const markRead = async () => {
    try {
      await fetch('/api/notifications/read/', {
        method: 'POST',
        headers: { 'Authorization': `Token ${user.token}` }
      });
      setUnread(0);
      setNotifs(prev => prev.map(n => ({ ...n, read: true })));
    } catch (err) { console.error(err); }
  };

  const notifIcons = { connection: '🤝', comment: '💬', vote: '👍', tip: '💰', message: '✉️', boost: '🚀' };

  return (
    <nav style={{ ...styles.nav }} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Logo */}
      <a href="/" style={styles.logo}>✍️ PenFlow</a>

      {/* Right side */}
      <div style={styles.right}>
        {/* Profile pic */}
        {user && (
          <a href={`/profile/${user.username}`} title={`@${user.username}`} data-nav="navAvatar" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', marginRight: '2px' }}>
            {user.avatar
              ? <img src={user.avatar} alt="me" style={{ width: '30px', height: '30px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #34d399' }} />
              : <div style={{ width: '30px', height: '30px', borderRadius: '50%', backgroundColor: '#34d399', color: '#0b1220', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '13px' }}>{user.username[0].toUpperCase()}</div>
            }
          </a>
        )}


        {/* Login button when logged out */}
        {!user && (
          <button onClick={onLogin} style={{ backgroundColor: '#34d399', color: '#3ea6ff', border: 'none', padding: '6px 14px', borderRadius: '20px', fontWeight: 'bold', fontSize: '12px', cursor: 'pointer' }}>🥧 Login</button>
        )}

        {/* Notification Bell */}
        {user && (
          <div style={{ position: 'relative' }}>
            <button style={styles.bellBtn} onClick={() => { setShowNotifs(!showNotifs); if (!showNotifs) markRead(); }}>
              🔔
              {unread > 0 && <span style={styles.badge}>{unread > 9 ? '9+' : unread}</span>}
            </button>
            {showNotifs && (
              <div style={styles.notifDropdown}>
                <div style={styles.notifHeader}>
                  <span style={{ fontWeight: 'bold' }}>🔔 {unread > 0 ? `${unread} Unread` : 'Notifications'}</span>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    {unread > 0 && <button style={{ backgroundColor: '#34d399', border: 'none', color: '#000', fontSize: '11px', padding: '4px 10px', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold' }} onClick={markRead}>✓ Mark all read</button>}
                    <button style={styles.notifClose} onClick={() => setShowNotifs(false)}>✕</button>
                  </div>
                </div>
                {notifs.length === 0 ? (
                  <div style={styles.notifEmpty}>No notifications yet</div>
                ) : (
                  notifs.slice(0, 10).map(n => (
                    <a key={n.id} href={n.link || '#'} style={{ ...styles.notifItem, backgroundColor: n.read ? 'white' : '#f3e5f5' }} onClick={() => setShowNotifs(false)}>
                      <span style={{ fontSize: '18px' }}>{notifIcons[n.type] || '🔔'}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '13px', color: '#333' }}>{n.message}</div>
                        <div style={{ fontSize: '11px', color: '#999' }}>{n.created_at}</div>
                      </div>
                    </a>
                  ))
                )}
                <div style={{ display: 'flex', borderTop: '1px solid #eee' }}>
                  <a href="/notifications" style={{ ...styles.notifFooter, flex: 1, borderTop: 'none' }} onClick={() => setShowNotifs(false)}>See all</a>
                  {notifs.length > 0 && <button style={{ ...styles.notifFooter, flex: 1, border: 'none', borderTop: 'none', cursor: 'pointer', color: '#ff4444', backgroundColor: 'white', borderLeft: '1px solid #eee' }} onClick={async () => { await fetch('/api/notifications/clear/', {method:'DELETE', headers:{'Authorization':'Token '+user.token}}); setNotifs([]); setUnread(0); setShowNotifs(false); }}>Clear all</button>}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Chat */}
        {user && (
          <a href="/inbox" style={{ backgroundColor: 'transparent', border: 'none', fontSize: '20px', cursor: 'pointer', padding: '4px', textDecoration: 'none' }}>💬</a>
        )}
        {/* Communities */}
        {user && (
          <a href="/communities" style={{ backgroundColor: 'transparent', border: 'none', fontSize: '20px', cursor: 'pointer', padding: '4px', textDecoration: 'none' }}>🌐</a>
        )}
        {/* Settings */}
        {user && (
          <a href="/settings" style={{ backgroundColor: 'transparent', border: 'none', fontSize: '20px', cursor: 'pointer', padding: '4px', textDecoration: 'none' }}>⚙️</a>
        )}

        {/* Hamburger */}
        <button style={styles.hamburger} onClick={() => setMenuOpen(!menuOpen)}>
          {menuOpen ? '✕' : '☰'}
        </button>
      </div>

      {/* Dropdown Menu */}
      {menuOpen && (
        <div style={styles.dropdown}>
          {user && <a href="/my-transactions" style={styles.menuItem} onClick={() => setMenuOpen(false)}>🧾 My Transactions</a>}
          <a href="/forum" style={styles.menuItem} onClick={() => setMenuOpen(false)}>🗣️ Forum</a>
          {user && <a href="/cart" style={styles.menuItem} onClick={() => setMenuOpen(false)}>🛒 My Cart</a>}
          {user && <a href="/dashboard" style={styles.menuItem} onClick={() => setMenuOpen(false)}>📋 My Dashboard</a>}
          {user && user.username === 'khalidxpert' && <a href="/admin" style={{ ...styles.menuItem, color: '#34d399', fontWeight: 'bold' }} onClick={() => setMenuOpen(false)}>⚙️ Admin Panel</a>}
          {user && user.username === 'khalidxpert' && <a href="/admin-dashboard" style={{ ...styles.menuItem, color: '#34d399', fontWeight: 'bold' }} onClick={() => setMenuOpen(false)}>📊 Unified Dashboard</a>}
          <a href="/" style={styles.menuItem} onClick={() => setMenuOpen(false)}>🏠 {tr('home')}</a>
          {user && <a href={`/profile/${user.username}`} style={styles.menuItem} onClick={() => setMenuOpen(false)}>👤 {tr('profile')}</a>}
          <a href="/leaderboard" style={styles.menuItem} onClick={() => setMenuOpen(false)}>🏆 {tr('leaderboard')}</a>
          {user && <a href="/connections" style={styles.menuItem} onClick={() => setMenuOpen(false)}>🤝 {tr('network')}</a>}
          {user && <a href="/ledger" style={styles.menuItem} onClick={() => setMenuOpen(false)}>📒 PenFlow Tips</a>}
          {user && <a href="/settings" style={styles.menuItem} onClick={() => setMenuOpen(false)}>⚙️ {tr('settings')}</a>}
          {user && <a href="/support" style={styles.menuItem} onClick={() => setMenuOpen(false)}>🎫 {tr('support')}</a>}
          <a href="/whitepaper" style={styles.menuItem} onClick={() => setMenuOpen(false)}>📄 Whitepaper</a>
          <a href="/penflow_whitepaper.pdf" target="_blank" rel="noopener noreferrer" style={styles.menuItem} onClick={() => setMenuOpen(false)}>📥 Download PDF</a>
          <div style={styles.menuDivider} />
          {user ? (
            <button onClick={() => { onLogout(); setMenuOpen(false); }} style={styles.menuLogout}>🚪 Logout (@{user.username})</button>
          ) : (
            <button onClick={() => { onLogin(); setMenuOpen(false); }} style={styles.menuLogin}>🔑 Login with Pi</button>
          )}
        </div>
      )}
    </nav>
  );
}

const styles = {
  nav: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 16px', backgroundColor: '#7a5c00', color: 'white',
    position: 'relative', zIndex: 1000,
  },
  logo: {
    fontSize: '20px', fontWeight: 'bold', color: 'white', textDecoration: 'none',
  },
  right: {
    display: 'flex', alignItems: 'center', gap: '10px',
  },
  profileLink: {
    display: 'flex', alignItems: 'center', textDecoration: 'none',
  },
  avatarImg: {
    width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover',
    border: '2px solid #34d399',
  },
  avatarPlaceholder: {
    width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#34d399',
    color: '#3ea6ff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 'bold', fontSize: '16px',
  },
  hamburger: {
    backgroundColor: 'transparent', border: '2px solid rgba(255,255,255,0.5)',
    color: 'white', fontSize: '18px', padding: '4px 10px', borderRadius: '8px',
    cursor: 'pointer', fontWeight: 'bold',
  },
  bellBtn: { backgroundColor: 'transparent', border: 'none', fontSize: '20px', cursor: 'pointer', position: 'relative', padding: '4px' },
  badge: { position: 'absolute', top: '-4px', right: '-4px', backgroundColor: '#ff4444', color: 'white', borderRadius: '50%', fontSize: '10px', width: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' },
  notifDropdown: {
    position: 'fixed',
    top: 60,
    right: 8,
    left: 'auto',
    width: 340,
    maxWidth: 'calc(100vw - 16px)',
    maxHeight: '70vh',
    overflowY: 'auto',
    background: '#fff',
    borderRadius: 12,
    boxShadow: '0 8px 30px rgba(0,0,0,0.18)',
    zIndex: 10000,
  },
  notifHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #eee', backgroundColor: '#7a5c00', color: 'white' },
  notifClose: { backgroundColor: 'transparent', border: 'none', color: 'white', cursor: 'pointer', fontSize: '16px' },
  notifItem: { display: 'flex', gap: '10px', padding: '10px 14px', borderBottom: '1px solid #eee', textDecoration: 'none', alignItems: 'flex-start' },
  notifEmpty: { padding: '20px', textAlign: 'center', color: '#999', fontSize: '14px' },
  notifFooter: { display: 'block', textAlign: 'center', padding: '10px', color: '#3ea6ff', textDecoration: 'none', fontSize: '13px', fontWeight: 'bold', borderTop: '1px solid #eee' },
  dropdown: {
    position: 'absolute', top: '100%', right: 0, left: 0,
    backgroundColor: '#7a5c00', boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
    display: 'flex', flexDirection: 'column', zIndex: 999,
    borderTop: '1px solid rgba(255,255,255,0.2)',
  },
  menuItem: {
    color: 'white', textDecoration: 'none', padding: '14px 20px',
    fontSize: '15px', borderBottom: '1px solid rgba(255,255,255,0.1)',
    display: 'block',
  },
  menuDivider: {
    height: '1px', backgroundColor: 'rgba(255,255,255,0.3)', margin: '4px 0',
  },
  menuLogout: {
    backgroundColor: 'transparent', color: '#ff8a80', border: 'none',
    padding: '14px 20px', fontSize: '15px', cursor: 'pointer', textAlign: 'left',
    width: '100%',
  },
  menuLogin: {
    backgroundColor: '#34d399', color: '#000', border: 'none',
    padding: '14px 20px', fontSize: '15px', cursor: 'pointer', textAlign: 'left',
    width: '100%', fontWeight: 'bold',
  },
};

export default Navbar;
